/* import __COLOCATED_TEMPLATE__ from './project-details-info-summary.hbs'; */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { createCache, getValue } from '@glimmer/tracking/primitives/cache';
import Component from '@glimmer/component';
import { task, enqueueTask } from 'ember-concurrency';
import { showModal } from 'sh-www/utils/modal-controls';
import { handleErrorResponseWithModal } from 'sh-www/utils/handles-errors';
import { stopClickEvent } from "sh-www/utils/events";
import { APPLICATION } from 'sh-www/models/application';
import { SH_BILLABLE_LINE_ITEM } from "sh-www/models/sh-billable-line-item";
import { PROJECT_LINE_ITEM } from "sh-www/models/project-line-item";
import {
  FEE_STRUCTURE,
  BUYER_COVER_SELLER_FEE_TYPES
} from 'sh-www/models/fee-structure';
import {
  PROJECT_DETAILS__APPLICATION_EDITABLE_STATES_BUYER,
  PROJECT_DETAILS__OTHER_ITEM_TYPES,
} from "sh-www/constants/routes/project-details";

const CLASS_NAME = 'SH-c-project-details-info-summary';

class ProjectDetailsInfoSummary extends Component {
  @service('commands/application-commands') applicationCommandService;
  @service('state-managers/application') applicationStateManager;
  @service('commands/company-commands') companyCommandService;
  @service('commands/project-line-item-commands') projectLineItemCommandService;
  @service session;
  @service store;
  @service media;

  @tracked coverCreatorFeesSliderToggled = false;
  @tracked coverExpensesSliderToggled = false;
  @tracked expediteCommitmentToggled = false;

  #currentCompanyConnection = createCache(() => {
    const currentUserCompanyId = this.session.currentUser.company?.id;
    if (!currentUserCompanyId) {
      return null;
    }

    const { seller } = this.args.application;
    return seller.companySellerConnections.find(({ companyId }) => currentUserCompanyId == companyId);
  });

  get className() {
    return CLASS_NAME;
  }

  get currentCompanyConnection() {
    return getValue(this.#currentCompanyConnection);
  }

  get validStatusForExpenseToggle() {
    const { status } = this.args.application;
    return status >= APPLICATION.STATUS.PENDING && status < APPLICATION.STATUS.COMPLETED;
  }

  get showExpediteToggle() {
    return this.session.currentUser.isBuyer;
  }

  get isExpediteLocked() {
    return this.args.application.expediteCommitment
      || this.args.application.baseShInvoice?.expedite;
  }

  get isInNetwork() {
    return this.currentCompanyConnection?.invited ?? false;
  }

  get showExpensesToggle() {
    const { currentUser } = this.session;
    const { company } = this.args.application;
    return currentUser.isBuyer &&
      company.canEnableExpenses &&
      this.validStatusForExpenseToggle &&
      !this.args.application.select;
  }

  get hasOfferBeenMade() {
    return this.args.application.price > 0;
  }

  get showCalculator() {
    const status = Number.parseInt(this.args.application.status);
    return this.session.currentUser.isSeller && status < APPLICATION.STATUS.ACTIVE && !this.args.application.sellerFeesCovered;
  }

  get showCoverCreatorFeesToggle() {
    const {
      sellerFeesCovered,
      status,
    } = this.args.application;
    const { currentUser } = this.session;
    const { isInNetwork } = this;

    return currentUser.isBuyer
      && (!currentUser.company.alwaysCoversCreatorFees || (!sellerFeesCovered && currentUser.company.alwaysCoversCreatorFees))
      && !isInNetwork
      && status < APPLICATION.STATUS.COMPLETED
      && status >= APPLICATION.STATUS.PENDING;
  }

  get showMarkActiveForExpenses() {
    const { isAllowExpenses, status } = this.args.application;
    return this.session.currentUser.isSeller
      && isAllowExpenses
      && (APPLICATION.STATUS.COMPLETE_PUBLISHER === status || APPLICATION.STATUS.COMPLETE_PRODUCER === status);
  }

  get disableCoverCreatorFeesToggle() {
    return Number.parseInt(this.args.application.status) >= APPLICATION.STATUS.ACTIVE;
  }

  get followersToShow(){
    return this.args.application.followers.toArray().sort((followerA, followerB) => {
      if (followerA.fullName > followerB.fullName) {
        return 1;
      } else if (followerA.fullName < followerB.fullName) {
        return -1;
      }

      return 0;
    });
  }

  get isCanTurnOffExpenses() {
    return this.session.currentUser.isBuyer
      && PROJECT_DETAILS__APPLICATION_EDITABLE_STATES_BUYER.includes(this.args.application.status)
      && !this.args.application.expenseProjectLineItems.length;
  }

  get cancellationLineItem() {
    return this.args.application.projectLineItems.find(lineItem => lineItem.type === PROJECT_LINE_ITEM.TYPES.CANCELLATION);
  }

  get cancellationAmount() {
    return this.cancellationLineItem
      ? this.args.application.price + this.cancellationLineItem.amount
      : 0;
  }

  get shBillableLineItems() {
    if (this.session.currentUser.isBuyer) {
      return this.args.application.shInvoiceLineItems;
    } else if (this.session.currentUser.isSeller) {
      return this.args.application.shPaymentLineItems;
    } else {
      const invoiceLineItems = this.args.application.shInvoiceLineItems.toArray();
      const paymentLineItems = this.args.application.shPaymentLineItems.toArray();
      return [...invoiceLineItems, ...paymentLineItems];
    }
  }

  get otherItemsToShow() {
    return this.shBillableLineItems.filter(billableLinItem =>  PROJECT_DETAILS__OTHER_ITEM_TYPES.includes(billableLinItem.type));
  }

  get postedByName() {
    return this.session.currentUser.id === this.args.application.buyer.id
      ? 'You'
      : this.args.application.buyer.fullName;
  }

  @action
  showCantTurnOffExpensesModal() {
    const modalModel = {
      actionButtonText: 'OK',
      isShowCancelButton: false,
      titleText: 'Can’t turn off expenses',
      bodyText: 'This freelancer already submitted expenses.<br><br>' +
        'Please confirm with the freelancer and have them remove expenses as needed.'
    };

    showModal(this, 'confirmation', modalModel);
  }

  @task
  *updatePriceTask(price) {
    const { application, loadPaymentData, loadThreadData } = this.args;
    const baseLineItem = application.projectLineItems.find(lineItem => lineItem.type === PROJECT_LINE_ITEM.TYPES.BASE);

    try {
      const response = yield this.projectLineItemCommandService.updateProjectLineItem(
        application,
        baseLineItem,
        price,
        baseLineItem.description
      );
      this.store.pushPayload(response);
      this.applicationStateManager.addToast('Project price changed');
      loadPaymentData.perform(application);
      loadThreadData.perform(application);
    } catch (e) {
      this.applicationStateManager.addToast('There was a problem while trying to change project price. Please try again.');
      this.errorTracker.error(error, this, `Error changing project price for application id "${application.id}" active`);
    }
  }

  @task
  *loadPotentialFollowersTask(companyId, subject) {
    const company = yield this.companyCommandService.loadCompany(companyId, ['approved-employees']);
    const employees = company?.approvedEmployees ?? [];

    return employees.filter(({ id }) => {
      return this.session.currentUser.id !== id &&  // current user not allowed in list
      subject.buyer.id !== id; // the buyer can NOT be removed
    });
  }

  @enqueueTask
  *saveFollowersTask(newUsersSelected, newUsersDeselected, subjectId) {
    const pending = [];

    if (newUsersSelected.length > 0) {
      pending.push(this.applicationCommandService.addFollowers(
        subjectId, newUsersSelected, ['followers']));
    }
    if (newUsersDeselected.length > 0) {
      pending.push(this.applicationCommandService.removeFollowers(
        subjectId, newUsersDeselected));
    }

    try {
      yield Promise.all(pending);
      this.applicationStateManager.addToast('Followers updated');
    } catch (error) {
      handleErrorResponseWithModal(
        this,
        error,
        'There was an error updating the followers. Please refresh and try again.'
      );
    }
  }

  @task
  *enableCoverCreatorFees(application) {
    try {
      yield this.applicationCommandService.setCoverSellerFeesEnabled(application);
      this.applicationStateManager.addToast('Cover creator fees enabled');
      this.args.loadThreadData.perform(application);
      this.args.loadPaymentData.perform(application);
    } catch (e) {
      this.applicationStateManager.addToast('Failed to enable covering creator fees. Try again or contact support for assistance.');
      this.setCoverCreatorFeesSliderToggled(false);
    }
  }

  @action
  onEditExpensesClicked() {
    const { application, loadPaymentData, loadThreadData } = this.args;
    const modalData = {
      application,
      onExpensesUpdated: () => {
        this.applicationStateManager.addToast('Expenses updated');
        loadThreadData.perform(application);
        loadPaymentData.perform(application);
      }
    };
    showModal(this, 'projects/edit-expenses-modal', modalData);
  }

  @action
  addFollowersClicked(e) {
    stopClickEvent(e);
    const { followers, company } = this.args.application;
    const modalModel = {
      title: 'Followers of this project',
      subTitle: 'Select who should be notified of any changes or messages on this project',
      loadPotentialFollowersTask: this.loadPotentialFollowersTask,
      currentFollowers: followers,
      saveFollowersTask: this.saveFollowersTask,
      companyId: company.id,
      subject: this.args.application
    };

    showModal(this, 'add-followers-modal', modalModel);
  }

  @action
  allowExpensesClicked() {
    const modalModel = {
      actionButtonText: 'Allow',
      titleText: 'Allow expenses',
      bodyText: 'Allow the creator to submit expenses as part of this project.',
      warningText: 'You cannot undo this action.',
      unsetToggle: () => this.setCoverExpensesSliderToggled(false),
      confirmAction: () => {
        this.toggleExpensesEnabled.perform(this.args.application, true);
      }
    };

    this.setCoverExpensesSliderToggled(true);
    showModal(this, 'projects/cover-fees-expenses-modal', modalModel);
  }

  @action
  expediteCommitmentClicked() {
    const application = this.args.application;

    const modalModel = {
      actionButtonText: 'Expedite payments',
      titleText: 'Expedite payments',
      showCreditsNotice: application.company.usesCredits,
      currentDays: application.maxExpectedPayoutDays,
      shInvoice: application.baseShInvoice,
      warningText: 'You cannot undo this action.',
      unsetToggle: () => this.setExpediteCommitmentSliderToggled(false),
      confirmAction: () => {
        this.enableExpediteCommitment.perform(application);
      }
    };

    this.setExpediteCommitmentSliderToggled(true);
    showModal(this, 'projects/expedite-commitment-modal', modalModel);
  }

  @action
  setCoverExpensesSliderToggled(toggleBool) {
    this.coverExpensesSliderToggled = toggleBool;
  }

  @action
  setExpediteCommitmentSliderToggled(toggleBool) {
    this.expediteCommitmentToggled = toggleBool;
  }

  @task
  *enableExpediteCommitment(application) {
    try {
      yield this.applicationCommandService.setExpediteCommitmentEnabled(application);
      this.applicationStateManager.addToast('Expediting payment');
      this.args.loadThreadData.perform(application);
      this.args.loadPaymentData.perform(application);
    } catch (e) {
      this.applicationStateManager.addToast('Failed to expedite. Try again or contact support for assistance.');
      this.setCoverCreatorFeesSliderToggled(false);
    }
  }

  @task
  *toggleExpensesEnabled(application, isEnabled) {
    try {
      yield this.applicationCommandService.setExpensesEnabled(application, isEnabled);
      this.applicationStateManager.addToast(isEnabled ? 'Expenses enabled' : 'Expenses disabled');
      this.args.loadThreadData.perform(application);
      this.args.loadPaymentData.perform(application);
    } catch (e) {
      this.applicationStateManager.addToast(`Failed to ${isEnabled ? 'enable' : 'disable'} expenses. Try again or contact support for assistance.`);
      this.setCoverExpensesSliderToggled(false);
    }
  }

  @action
  coverCreatorFeesClicked() {
    const { feeStructure, price, select, consolidatedBuyerFees, consolidatedSellerFees } = this.args.application;

    const buyerServiceFees = consolidatedBuyerFees.filter(({ type }) => type !== SH_BILLABLE_LINE_ITEM.LINE_ITEM_FEE_TYPES.BASE_FEE);
    const buyerServiceFeesPercent = feeStructure.buyerFlatBaseFee ? 0 : feeStructure.buyerBaseFee * 100;

    const creatorFees = consolidatedSellerFees.reduce((total, fee) => {
      return SH_BILLABLE_LINE_ITEM.BUYER_COVER_SELLER_FEE_TYPES.includes(fee.type)
        ? total + Math.abs(fee.amount)
        : total;
    }, 0);

    const creatorFeesPercent = BUYER_COVER_SELLER_FEE_TYPES.reduce((total, fee) => {
      // Only add select fee if application is a select project
      if (fee === FEE_STRUCTURE.SELLER_FEES[FEE_STRUCTURE.FEE_TYPES.SELECT_FEE] && !select) {
        return total;
      }
      return feeStructure[fee] ? total + (feeStructure[fee] * 100) : total;
    }, 0);

    const modalModel = {
      actionButtonText: 'Cover fees',
      titleText: 'Cover creator fees',
      bodyText: "Pay the creator's fees as part of your costs so the creator doesn't have to.",
      warningText: 'You cannot undo this action.',
      currentProjectPrice: price,
      creatorFee: creatorFees,
      creatorFeePrecentage: creatorFeesPercent,
      buyerFee: buyerServiceFees?.[0]?.amount ?? 0,
      buyerFeePercentage: buyerServiceFeesPercent,
      unsetToggle: () => this.setCoverCreatorFeesSliderToggled(false),
      confirmAction: () => {
        this.enableCoverCreatorFees.perform(this.args.application);
      }
    };

    this.setCoverCreatorFeesSliderToggled(true);
    showModal(this, 'projects/cover-fees-expenses-modal', modalModel);
  }

  @action
  setCoverCreatorFeesSliderToggled(toggleBool) {
    this.coverCreatorFeesSliderToggled = toggleBool;
  }

  @action
  changePriceClicked() {
    const modalModel = {
      application: this.args.application,
      onChangePriceConfirmed: (price) => {
        this.updatePriceTask.perform(price);
      }
    };
    showModal(this, 'projects/change-project-price-modal', modalModel);
  }
}

export default ProjectDetailsInfoSummary;
